import * as React from 'react';
import './metadataForm.scss';
import { Alert as A } from 'rcd-cpp-ui/ui/alert/Alert';
import { Metadata, Schema, SchemaField } from '../../types/Metadata';
import { DataType } from '../../enums/DataType';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { useActions } from './metadataFormActions';
import { Buttons, Primary } from 'rcd-cpp-ui/ui/button';
import { Slider } from '../Slider/slider';
import { NodeType } from '../../enums/NodeType';
import { CancelX, Form } from '../Icon/icon';
import * as ItemTypeIcon from '../Icon/itemTypeIcon';
import AccessControl from '../AccessControl/accessControl';
import { ShowFields } from './ShowFields/showFields';
import { StatusBar } from './StatusBar/statusBar';
import { StatusField } from './StatusField/StatusField';
import { ValidationAlert } from './ValidationAlert/validationAlert';
import { useLoader } from '../Loader/loader';
import { useAlert } from '../Alert/alert';
import {
  generateHashCode,
  getShowFieldsOption,
  getFieldsToDisplay,
  getItemContentTypeId,
  getItemStatusId,
  getItemType,
  getSchemaForm,
  isUniMDChild
} from '../../utils';
import { CommentForm } from '../CommentForm/commentForm';
import URLManager from '../../utils/urlManager';
import { ToasterWithLink, useToaster } from '../Toaster/toaster';
import { ItemStatus } from '../../enums/ItemStatus';
import { TriggerLocation } from '../../enums/TriggerLocation';
import { useFullStory } from '../App/fullStory';
import { FSEvent } from '../../enums/FSEvent';
import { Icon } from 'rcd-cpp-ui/ui/icon';
import { ContentMetricsForm } from '../ContentMetricsForm/ContentMetricsForm';
import { Resource } from '../../enums/Resource';
import { useSelector } from '../../utils/useSelector';
import { BacktagForm } from '../BackTagForm/backTagForm';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { CustomDataAlert } from './ValidationAlert/CustomDataAlert';
import { Field } from './metadataFormField';
import { FieldType } from '../../enums/FieldType';
import { FieldData, FormUpdater } from '../../types/Field';
import { CopyFromPreviousButton } from './CopyPreviousButton/copyPreviousButton';
import { ShowFieldsOption } from '../../enums/ShowFieldsOption';
import { useIsAuthorPromotable } from '../../utils/useIsAuthorPromotable';
import { useWebSocketActions } from '../../services/webSocketActions';

let fieldData: FieldData[] = [];
export const getUpdater = (
  {
    updateField,
    updateData,
    updateFields
  }: Pick<FormUpdater, 'updateData' | 'updateField' | 'updateFields'>,
  autoSave: boolean
): FormUpdater => ({
  reset: () => {
    fieldData = [];
  },
  update: async () => {
    if (!fieldData?.length) return;
    if (autoSave && updateData) {
      const toUpdate = [...fieldData];
      fieldData = [];
      await updateData(toUpdate);
    }
  },
  updateData: (f: SchemaField | FieldData[], v?: any) => {
    if (autoSave && updateData) updateData(f, v);
    fieldData = [];
  },
  updateField: async (field: SchemaField, value: any) => {
    const fields = [{ field, value }];
    fieldData = [...getFieldData(fields), ...fields];
    await updateField(field, value).catch(e => {
      fieldData = getFieldData(fields);
      throw e;
    });
  },
  updateFields: async fields => {
    fieldData = [...getFieldData(fields), ...fields];
    await updateFields(fields).catch(e => {
      fieldData = getFieldData(fields);
      throw e;
    });
  }
});
const getFieldData = (excludeFields: FieldData[]) => {
  const keys = excludeFields.map(f => f.field.key);
  return fieldData.filter(fd => !keys.includes(fd.field.key));
};

const getRequiredLegend = (data?: Partial<Metadata>, useSeeAll?: boolean) => {
  const excludeNext = !(
    data?.labels?.includes(NodeType.Item) &&
    (data?.props?.itemId ||
      data?.props?.status?.filter((s: Metadata) => s?.props?.id == 12).length >
        0)
  );
  return (
    <>
      <div className="required-details">
        {useSeeAll && (
          <span className={'show-fields-toggler'}>
            <ShowFields />
          </span>
        )}
        {excludeNext && <span>Indicates Required to Promote</span>}
        {excludeNext && <span className="required-next-asterisks">| **</span>}
        <span>Required Field</span>
        <span className="required-asterisk">*</span>
      </div>
    </>
  );
};

export const MetadataForm = () => {
  const data = useSelector(s => s.form?.data);
  const dataRefId = data?.props?.refId;
  const schema = useSelector(s => s.form?.schema);
  const shouldValidate = useSelector(s => s.form?.shouldValidate);
  const pushResponse = useSelector(s => s.form?.pushResponse);
  const comments = useSelector(s => s.form?.comments);
  const workspace = useSelector(s => s.planning?.selectedWorkspace);
  const schemas = workspace?.schemas;
  const userSettings = useSelector(s => s.planning?.userSettings);
  const getAPI = useSelector(s => s.app?.elFn?.getAPI);
  const { subscribeToData, unsubscribeFromData } = useWebSocketActions();
  const {
    showForm,
    closeForm,
    slideOut,
    createComment,
    updateComment,
    deleteComment,
    loadComments,
    getAutoAcceptImpliedDataProps,
    ...actions
  } = React.useMemo(() => useActions(), []);
  const [impliedDataProps, setImpliedDataProps] = React.useState({});
  const { captureEvent } = useFullStory();
  const { id, c: cid, updateUrl, m: mid } = URLManager.useRouter();
  const { createToaster } = useToaster(ToasterWithLink, { updateUrl });
  const {
    updateData,
    updateField,
    updateFields,
    validateWorkspace,
    ...loadingActions
  } = useAlert(actions);
  const {
    isLoading,
    setLoading,
    Loader,
    message,
    setMessage,
    Alert,
    create,
    validate
  } = useLoader(loadingActions);
  const [readonlyFields, setReadonlyFields] = React.useState<string[]>([]);
  const [showBacktagForm, setShowBacktagForm] = React.useState<boolean>(false);
  const dataHash = generateHashCode(JSON.stringify(data?.props));
  const schemaForm = getSchemaForm(schema, data);
  React.useEffect(() => {
    if (!!data) {
      getAutoAcceptImpliedDataProps(data, schemaForm).then(setImpliedDataProps);
    } else {
      setImpliedDataProps({});
    }
  }, [dataHash]);
  React.useEffect(() => {
    if (!data) {
      setLoading(false);
      setMessage(null);
    } else {
      if (data?.readonlyFields?.length) {
        setReadonlyFields(data.readonlyFields);
      }
    }
  }, [data]);

  let alert = null;
  if (data && !schema)
    alert = <A type="error">No Schema for {data.labels.join(':')}.</A>;
  const statusId = getItemStatusId(data);
  const itemType = getItemType(data);
  const contentTypeId = getItemContentTypeId(data);
  const isAuthorPromotable = useIsAuthorPromotable();
  const showFieldsOption = getShowFieldsOption(data, userSettings);
  const allErrorFields = useSelector(s => s.form?.allErrorFields);
  const { fields, allFields } = React.useMemo(() => {
    const retirementFields = [];
    if ([ItemStatus.PendingRetirement, ItemStatus.Retired].includes(statusId)) {
      const retirementReasonField = findOne(schema.props.fields, {
        field: 'retirementReason'
      });
      const retirementMarketCoverageField = findOne(schema.props.fields, {
        field: 'retirementMarketCoverage'
      });
      retirementFields.push({ ...retirementReasonField, isHidden: false });
      retirementFields.push({
        ...retirementMarketCoverageField,
        isHidden: false
      });
    }
    const allFields = getFieldsToDisplay(
      schema,
      data,
      showFieldsOption,
      impliedDataProps,
      Array.from(allErrorFields ?? [])
    ).concat(retirementFields);
    const titleField = allFields.find(f => f?.key === 'Item.title');

    const groupFields = [];
    const nonGroupFields = [];

    for (const field of allFields.filter(f => f?.key !== 'Item.title')) {
      if (field.dataType == DataType.group) {
        groupFields.push(field);
      } else if (!field.parentField && field?.dataType != DataType.link) {
        if (field?.key == 'Item.status' && !dataRefId) {
          continue;
        }
        nonGroupFields.push(field);
      }
    }

    const fieldsToRender = [];
    if (titleField) fieldsToRender.push(titleField);
    fieldsToRender.push(...groupFields);

    if (nonGroupFields.length > 0) {
      if (groupFields.length > 0) {
        const additionalInfoField = {
          field: 'additionalItemDetails',
          dataType: DataType.group,
          fieldType: FieldType.custom,
          name: 'Additional Item Details',
          displayName: 'Additional Item Details',
          key: 'additionalItemDetails',
          showAt: undefined,
          order: 999,
          parentField: undefined,
          isAdditionalInfoGroup: true,
          childFields: nonGroupFields
        } as any;
        fieldsToRender.push(additionalInfoField);
      } else {
        fieldsToRender.push(...nonGroupFields);
      }
    }

    return { fields: fieldsToRender, allFields };
  }, [
    schema,
    userSettings,
    pushResponse,
    impliedDataProps,
    statusId,
    itemType,
    contentTypeId,
    allErrorFields
  ]);

  if (data && !fields?.length && !alert)
    alert = (
      <A type="error">{`No ${[ShowFieldsOption.RequiredForPhase, ShowFieldsOption.RequiredToPromote].includes(showFieldsOption) ? 'Required' : ''} fields for ${data.labels.join(':')}.`}</A>
    );
  const disabled = false;

  const updater = React.useMemo(
    () => getUpdater({ updateField, updateData, updateFields }, !!dataRefId),
    [dataRefId]
  );
  React.useEffect(() => {
    updater.reset();
  }, []);

  const { searchLucene } = getAPI();

  const [refIdError, setRefIdError] = React.useState(null);

  React.useEffect(() => {
    if (!!id && dataRefId != id && id != 'newitem') {
      setMessage(null);
      showForm(id, null, cid, mid).catch(e => {
        setRefIdError(e?.message);
      });
    }
    if (!id) {
      setMessage(null);
      setShowBacktagForm(false);
      closeForm();
    }
  }, [id, cid, mid]);
  React.useEffect(() => {
    if (!!dataRefId && !message) {
      validateWorkspace();
    }
  }, [dataRefId, workspace?.props?.refId]);
  React.useEffect(() => {
    if (dataRefId) {
      subscribeToData(dataRefId);
    }
    return () => {
      if (dataRefId) {
        unsubscribeFromData(dataRefId);
      }
    };
  }, [dataRefId]);

  const isCreateInContent = React.useMemo(() => {
    if (
      !dataRefId &&
      schema?.props?.type == NodeType.Item &&
      isUniMDChild(data)
    ) {
      return getItemStatusId(data) == ItemStatus.Concept_Review;
    }
    return false;
  }, [
    dataRefId,
    schema?.props?.type,
    data?.props?.status,
    data?.props?.contentType,
    data?.props?.uniOf
  ]);

  if (!workspace) return null;

  return (
    <>
      {data && !dataRefId && (
        <Modal
          id="metadataFormModal"
          onClose={() => {
            closeForm();
            setReadonlyFields([]);
          }}
          header={
            <NewItemHeader
              data={data}
              schema={schema}
              schemaForm={schemaForm}
              itemType={itemType}
              updater={updater}
            />
          }
          footer={
            <Buttons>
              {data?.triggerLocation != TriggerLocation.CONTEXTUALIZATION &&
                data?.triggerLocation != TriggerLocation.UNI && (
                  <Primary
                    label={'Add Another'}
                    onClick={async () => {
                      if (schema.props.type == NodeType.Item) {
                        const triggerLocation = data.triggerLocation;
                        captureEvent(FSEvent.AddItem, { triggerLocation });
                        await create(createToaster(), true);
                        setMessage(null);
                      } else {
                        await create(null, true);
                        setMessage(null);
                      }
                    }}
                    disabled={isLoading}
                  />
                )}
              <Primary
                onClick={() => {
                  if (schema.props.type == NodeType.Item) {
                    const triggerLocation = data.triggerLocation;
                    const parentStatusId = data.parentStatusId;
                    const parentStatusText = data.parentStatusText;
                    captureEvent(FSEvent.AddItem, { triggerLocation });
                    if (
                      triggerLocation == TriggerLocation.UNI &&
                      data?.props?.uniOf?.length
                    ) {
                      captureEvent(FSEvent.UNI, {
                        triggerLocation: TriggerLocation.UPDATE,
                        parentStatusId,
                        parentStatusText
                      });
                    }
                    if (isCreateInContent) {
                      validate().then(errFields => {
                        if (!errFields?.size) {
                          create(createToaster(), false, true);
                        }
                      });
                    } else {
                      create(createToaster());
                    }
                  } else {
                    create(null);
                  }
                }}
                label={isCreateInContent ? 'Push to Production' : 'Add'}
                disabled={isLoading}
              />
            </Buttons>
          }
        >
          <Loader spinnerSize="icon" />
          <div className="form-legend-grouping">
            <ValidationAlert impliedDataProps={impliedDataProps} />
            <CustomDataAlert />
            <Alert />
            <div>
              <CopyFromPreviousButton updateFields={updateFields} />
            </div>
            <div className="modal-legend">{getRequiredLegend(null, true)}</div>
            {alert || (
              <div className="metadata-form scrollable">
                {fields.map(field => (
                  <Field
                    key={field.field}
                    field={field}
                    data={data}
                    impliedDataProps={impliedDataProps}
                    schema={schema}
                    schemas={schemas}
                    disabled={disabled || readonlyFields?.includes(field.key)}
                    updater={updater}
                    shouldValidate={shouldValidate}
                    pushResponse={pushResponse}
                    userSettings={userSettings}
                    isModal={true}
                  />
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}
      <Slider
        className={`metadata-form-slider ${
          !!cid || !!mid || showBacktagForm ? ' wide' : ''
        }`}
        show={!!dataRefId || !!refIdError?.length}
        onClickOutside={e => {
          setRefIdError('');
          const c = getParent(e?.target, 'slider-activator');
          const isToaster = e?.target.getAttribute('id') == 'cppToasters';
          updater.update();
          if (!c && !isToaster) {
            updateUrl({ id: null });
            slideOut();
          }
          setShowBacktagForm(false);
        }}
      >
        {(!!dataRefId || !!refIdError?.length) && (
          <>
            <div className="metadata-form-header">
              <ItemHeader data={data} schema={schema} />
              <StatusBar data={data} fields={allFields} updater={updater} />
              <div className="slider-close">
                <Icon
                  onClick={() => {
                    setRefIdError('');
                    updateUrl({ id: null });
                    updater.update();
                    slideOut();
                  }}
                >
                  <CancelX />
                </Icon>
              </div>
            </div>
            <ValidationAlert impliedDataProps={impliedDataProps} />
            <CustomDataAlert />
            {!!refIdError?.length && <A type="error">{refIdError}</A>}
            <Alert />
            {isAuthorPromotable && (
              <A type="info">No approval needed – push to Draft in CPP Production now</A>
            )}
            <div>
              <div className="form-container">
                <div className="form-legend-grouping">
                  <div className="slider-legend">
                    {getRequiredLegend(data, true)}
                  </div>
                  {!!dataRefId &&
                    (alert || (
                      <div className="metadata-form scrollable" key={dataRefId}>
                        {fields.map(field => (
                          <Field
                            key={field.field}
                            field={field}
                            data={data}
                            impliedDataProps={impliedDataProps}
                            schema={schema}
                            schemas={schemas}
                            disabled={disabled}
                            updater={updater}
                            shouldValidate={shouldValidate}
                            pushResponse={pushResponse}
                            userSettings={userSettings}
                          />
                        ))}
                      </div>
                    ))}
                </div>
                <div className="widget-container">
                  {!refIdError?.length && (
                    <AccessControl
                      resource={Resource.commentWidget}
                      data={data}
                    >
                      <CommentForm
                        comments={comments}
                        createComment={createComment}
                        updateComment={updateComment}
                        deleteComment={deleteComment}
                        getComments={loadComments}
                        showHideCommentWidget={() => {
                          if (!cid) {
                            updateUrl({ id, c: 'true' });
                          } else {
                            updateUrl({ id });
                          }
                          setShowBacktagForm(false);
                        }}
                        shouldShowComments={!!cid}
                        searchUsers={async query => {
                          const updatedQuery = query
                            .split(' ')
                            .map(p => p + '*')
                            .join(' OR ');
                          const searchResults =
                            await searchLucene(updatedQuery);
                          return searchResults
                            .filter(r => r.empCd && r.empName && r.empEmail)
                            .map(r => {
                              return { ...r, id: r.empCd, display: r.empEmail };
                            });
                        }}
                      />
                    </AccessControl>
                  )}
                  {showContentMetric(data) && (
                    <ContentMetricsForm
                      showHideContentMetricsWidget={() => {
                        if (!mid) {
                          updateUrl({ id, m: 'true' });
                          captureEvent(FSEvent.ViewContentMetrics);
                        } else {
                          updateUrl({ id });
                        }
                        setShowBacktagForm(false);
                      }}
                      updater={updater}
                      shouldShowMetrics={!!mid}
                    />
                  )}
                  {
                    <AccessControl
                      resource={Resource.backtagWidget}
                      data={data}
                    >
                      <BacktagForm
                        showHideBacktagWidget={() => {
                          updateUrl({ id });
                          setShowBacktagForm(!showBacktagForm);
                        }}
                        shouldShowBacktags={showBacktagForm}
                        updater={updater}
                        isMetricsVisible={showContentMetric(data)}
                      />
                    </AccessControl>
                  }
                </div>
              </div>
            </div>
          </>
        )}
      </Slider>
    </>
  );
};

type ItemHeaderProps = { data: Partial<Metadata>; schema: Schema };

const ItemHeader = ({ data, schema }: ItemHeaderProps) => {
  const itemType = getItemType(data);
  const FormIcon = ItemTypeIcon[itemType] || Form;
  let description = `${schema?.props?.name} Details`;
  const itemField = findOne(schema?.props?.fields, { key: 'Item.itemType' });
  if (itemField?.enumValues) {
    const value = itemField.enumValues[itemType];
    if (value) description = value;
  }
  return (
    <div>
      <FormIcon />
      <span>{description}</span>
    </div>
  );
};

type NewItemHeaderProps = {
  data: Partial<Metadata>;
  schema: Schema;
  schemaForm: Schema;
  itemType: string;
  updater: FormUpdater;
};

const NewItemHeader = ({
  data,
  schema,
  schemaForm,
  itemType,
  updater
}: NewItemHeaderProps) => {
  const FormIcon = ItemTypeIcon[itemType];

  if (schema.props.type !== NodeType.Item) {
    return <>{`Add ${schema?.props?.name || data?.labels?.join(':')}`}</>;
  }

  const statusField = schemaForm?.props?.fields?.find(
    f => f?.key == 'Item.status'
  );

  return (
    <div className="new-item-header">
      <div className="new-item-header-container">
        <FormIcon />
        <div>
          New Planning Item:{' '}
          {data?.props?.itemType == 'content' ? (
            <span className="bold-text">Content Item</span>
          ) : (
            <span className="bold-text">Global Submission</span>
          )}{' '}
        </div>
      </div>
      <div className="new-item-header-status">
        {statusField && (
          <StatusField
            field={statusField}
            data={data}
            schema={schema}
            onChange={async newStatus => {
              updater.updateField(statusField, [newStatus]);
            }}
          />
        )}
      </div>
    </div>
  );
};

const showContentMetric = (data: Partial<Metadata>) => {
  const status = getItemStatusId(data);
  return (
    status == ItemStatus.Published ||
    status == ItemStatus.PendingRepublish ||
    status == ItemStatus.PublishedHistoricalNonCPP ||
    (status >= 3000 && status <= 4000)
  );
};

const hasMatch = (d: Metadata, txt: string) => {
  if (!d?.props?.isActive) return false;
  if (d?.props?.name?.toLowerCase().indexOf(txt.toLowerCase()) >= 0)
    return true;
  if (d.metadata) {
    for (const m of d.metadata) {
      if (hasMatch(m, txt)) return true;
    }
  }
  return false;
};

const getParent = (el: HTMLElement, className: string): HTMLElement => {
  const c = el.getAttribute('class');
  if (c?.split(' ').indexOf(className) >= 0) {
    return el;
  }
  if (el.parentElement) {
    return getParent(el.parentElement, className);
  }
  return null;
};
